"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { FrameList } from "@/components/tools/lit-essays/frame-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  blankParagraph,
  buildLeqOutline,
  buildLeqThesisFrames,
  LEQ_COMPLEXITY_MOVES,
  LEQ_MAX_PARAGRAPHS,
  LEQ_MIN_PARAGRAPHS,
  LEQ_THESIS_FRAMES,
  type LeqComplexityMove,
  type LeqInput,
  type LeqParagraph,
  REASONING_PROCESSES,
  type ReasoningProcess,
} from "@/lib/tool-kit/lit-essays/leq-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const LEQ_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  promptHeading: "The prompt",
  promptLabel: "LEQ prompt",
  promptPlaceholder:
    "Paste the question, e.g. “Evaluate the extent to which … changed from … to …”",
  processLabel: "Reasoning process the prompt asks for",
  processes: {
    comparison: "Comparison",
    causation: "Causation",
    ccot: "Continuity and change over time",
  },
  processHint:
    "Look at the task words: “compare” points to comparison, “causes” or “effects” to causation, and “extent of change” to continuity and change.",
  introHeading: "Introduction",
  contextLabel: "Contextualization — a broader historical context",
  contextPlaceholder:
    "Describe events, developments, or processes before, during, or after the time frame of the prompt.",
  contextHint:
    "Needs more than a phrase. Keep a different example for your evidence.",
  thesisLabel: "Thesis — a claim with a line of reasoning",
  thesisPlaceholder:
    "Answer the prompt and give your reasons or categories. Do not restate the question.",
  framesHeading: "Thesis frames",
  framesIntro:
    "Fill-in-the-blank frames for this reasoning process, with your paragraph categories worked in — honest templates, no AI.",
  framesNeed: "Name your body paragraph categories to see thesis frames.",
  frameLabels: ["Frame 1", "Frame 2"],
  useAsThesis: "Use as my thesis",
  copy: "Copy",
  copied: "Copied!",
  bodyHeading: "Body paragraphs",
  paragraphLegend: "Body paragraph {n}",
  categoryLabel: "Category (the idea this paragraph covers)",
  categoryPlaceholder: "e.g. Market integration",
  claimLabel: "Topic sentence — the paragraph's claim",
  claimPlaceholder: "One sentence that supports your thesis.",
  evidenceLabel: "Specific evidence — one example per line",
  evidencePlaceholder:
    "e.g. The Erie Canal (1825) linked the Great Lakes to New York City.",
  evidenceHint:
    "Name each example and say what it was. A name alone, such as “the Erie Canal”, is too thin.",
  explanationLabel: "Reasoning — how the evidence proves the claim",
  explanationPlaceholder:
    "Use your reasoning process: say what the examples show about change, cause, or comparison.",
  addParagraph: "Add a body paragraph",
  removeParagraph: "Remove",
  removeParagraphLabel: "Remove body paragraph {n}",
  complexityHeading: "Complexity",
  complexityMoveLabel: "How will you show complex understanding?",
  complexityNone: "Not decided yet",
  complexityNoteLabel: "Your plan, in a sentence or two",
  complexityNotePlaceholder:
    "Say what you will qualify, compare, or connect, and which examples you will use.",
  complexityMoves: {
    perspectives: "Explain multiple themes or perspectives to show nuance",
    multiple:
      "Explain multiple causes or effects, similarities or differences, or continuities or changes",
    both: "Explain both cause and effect, both similarity and difference, or both continuity and change",
    connections:
      "Make insightful connections within and across periods or regions",
    fourPieces:
      "Explain how at least four specific pieces of evidence support a nuanced argument",
    differentViews: "Use evidence to show different perspectives",
  },
  fileName: "leq-outline",
  document: {
    heading: "Your LEQ outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the outline has your content.",
  },
  docTitle: "LEQ Outline",
  subtitle: "AP History long essay question · six-point rubric",
  headings: {
    prompt: "Prompt and reasoning process",
    introduction: "Introduction",
    body: "Body paragraph {n}: {category}",
    complexity: "Complexity",
    rubric: "Planning check against the six-point rubric",
  },
  items: {
    process: "Reasoning process",
    context: "Contextualization",
    thesis: "Thesis",
    claim: "Topic sentence",
    evidence: "Evidence",
    explanation: "Reasoning",
    link: "Link",
    move: "Approach",
  },
  processGuidance: {
    comparison:
      "Comparison: describe and explain similarities and differences, then say why they existed or which matters more. Cover both, not only one.",
    causation:
      "Causation: explain causes and effects and how they connect. Separate primary from secondary causes and short-term from long-term effects, and judge their relative importance.",
    ccot: "Continuity and change over time: say what changed and what stayed the same across the period, explain the pattern, and weigh which mattered more.",
  },
  claimPrompts: {
    comparison:
      "Topic sentence — state a similarity or a difference for this category.",
    causation:
      "Topic sentence — name a cause (or an effect) in this category and its importance.",
    ccot: "Topic sentence — state what changed, or what continued, in this category.",
  },
  explanationPrompts: {
    comparison:
      "Reasoning — explain why the similarity or difference existed and why it matters.",
    causation:
      "Reasoning — explain how the cause led to the result, and whether it was short-term or long-term, primary or secondary.",
    ccot: "Reasoning — explain how the examples show change or continuity, and what drove it.",
  },
  prompts: {
    prompt:
      "Paste the LEQ prompt here, and underline the task word and the time frame.",
    context:
      "Contextualization — describe a broader event, development, or process before, during, or after the time frame. More than a phrase.",
    thesis:
      "Thesis — answer the prompt with a claim and a line of reasoning or categories. Do not restate the question.",
    evidence:
      "Evidence — name at least two specific examples and say what each was.",
    link: "Link — tie the paragraph back to the thesis.",
    complexity:
      "Choose how you will show complex understanding, and write the sentence or two that does it.",
  },
  guidance: {
    context:
      "The thesis must be in one place, in the introduction or the conclusion. Contextualization may appear anywhere in the essay.",
    evidence:
      "Two specific examples relevant to the prompt earn the first evidence point; using at least two to support an argument earns the second.",
  },
  rubricNote:
    "A planning check against the AP History LEQ rubric (six points), not a score. The word-count tests are rough proxies for “specific” and “more than a phrase”; a reader scoring the essay decides.",
  plannedLine: "Your outline is set up for {planned} of 6 points.",
  rubric: {
    thesis:
      "Thesis/claim (1 point): a claim that answers the prompt, not a restatement",
    contextualization:
      "Contextualization (1 point): a broader context, more than a phrase",
    evidenceOne:
      "Evidence (1st point): at least two specific examples relevant to the prompt",
    evidenceTwo:
      "Evidence (2nd point): at least two examples used to support an argument",
    reasoning:
      "Analysis and reasoning (1st point): the process frames at least two body paragraphs",
    complexity:
      "Analysis and reasoning (2nd point): complex understanding built into the argument",
  },
  and: "and",
  categoryStand: "[category {n}]",
  example: {
    prompt:
      "Evaluate the extent to which the Transportation Revolution changed the United States economy from 1815 to 1860.",
    process: "ccot",
    context:
      "After the War of 1812, national leaders promoted economic development, and a growing western population and expanding northern cities created demand for faster, cheaper ways to move goods and people.",
    thesis:
      "The Transportation Revolution changed the United States economy to a great extent between 1815 and 1860, because canals, steamboats, and railroads linked regional markets and let industry and commercial farming expand, although the South’s economy remained tied to slave-grown cotton.",
    complexityMove: "both",
    complexityNote:
      "I will qualify the thesis by showing that transportation changed the North and West far more than the South, and that the South’s continuity was itself part of the new national market, since cotton moved through it.",
  },
  exampleParagraphs: {
    category: [
      "Market integration",
      "Industry and cities",
      "Continuity in the South",
    ],
    claim: [
      "New transportation linked western farms, eastern cities, and overseas buyers into a national market economy.",
      "The new transportation helped spur commercial growth and urban expansion in the North.",
      "Despite the new transportation, the South’s economy remained rooted in cotton and slavery, which limits how much the whole economy changed.",
    ],
    evidence: [
      "The Erie Canal (opened 1825) connected the Great Lakes to the Hudson River and sharply cut the cost of shipping goods between the Midwest and New York City.\nSteamboats, such as Robert Fulton’s Clermont (1807), spread across western rivers after 1815 and made upstream shipping practical.",
      "New York City, the Erie Canal’s seaport terminus, became the nation’s leading commercial center and port.\nBy the 1850s, railroads linked eastern ports with the Midwest, and Chicago grew into a rail hub.",
      "Cotton remained the nation’s leading export in the 1850s.\nThe enslaved population grew from about 1.5 million in 1820 to nearly 4 million by 1860.",
    ],
    explanation: [
      "Cheaper, faster shipping let farmers sell crops in distant markets and let eastern merchants supply western buyers, so regions began to specialize and depend on each other, a major change from a largely local economy.",
      "Transportation gave cities access to wider markets and raw materials, which changed where people lived and worked and increased the share of the economy in trade and manufacturing.",
      "These examples show continuity: the region’s labor system and its main product did not change, so the transformation was uneven across regions.",
    ],
  } as Columns<"category" | "claim" | "evidence" | "explanation">,
} as const;

type Strings = typeof LEQ_OUTLINE_GENERATOR_STRINGS;

/** The example's body paragraphs, text only; English fills any short translated column. */
export function leqExampleParagraphs(
  strings: Strings
): Omit<LeqParagraph, "id">[] {
  return zipColumns(
    LEQ_OUTLINE_GENERATOR_STRINGS.exampleParagraphs,
    strings.exampleParagraphs
  );
}
type TextKey = "prompt" | "context" | "thesis" | "complexityNote";

const BLANK: Pick<LeqInput, TextKey | "complexityMove"> = {
  prompt: "",
  context: "",
  thesis: "",
  complexityMove: "",
  complexityNote: "",
};

const DEFAULT_PARAGRAPHS = 2;

function initialParagraphs(): LeqParagraph[] {
  return Array.from({ length: DEFAULT_PARAGRAPHS }, (_, index) =>
    blankParagraph(index + 1)
  );
}

function ParagraphFields({
  paragraph,
  index,
  strings,
  removable,
  onChange,
  onRemove,
}: {
  paragraph: LeqParagraph;
  index: number;
  strings: Strings;
  removable: boolean;
  onChange: (change: Partial<LeqParagraph>) => void;
  onRemove: () => void;
}) {
  const fmt = useToolNumber();
  const id = paragraph.id;
  return (
    <fieldset className={`${CARD} space-y-4`}>
      <legend className="px-1 font-medium text-foreground text-sm">
        {t(strings.paragraphLegend, { n: fmt(index + 1) })}
      </legend>
      <TextField
        id={`leq-category-${id}`}
        label={strings.categoryLabel}
        onChange={(category) => onChange({ category })}
        placeholder={strings.categoryPlaceholder}
        value={paragraph.category}
      />
      <TextAreaField
        id={`leq-claim-${id}`}
        label={strings.claimLabel}
        onChange={(claim) => onChange({ claim })}
        placeholder={strings.claimPlaceholder}
        prose
        rows={2}
        value={paragraph.claim}
      />
      <TextAreaField
        hint={strings.evidenceHint}
        id={`leq-evidence-${id}`}
        label={strings.evidenceLabel}
        onChange={(evidence) => onChange({ evidence })}
        placeholder={strings.evidencePlaceholder}
        prose
        rows={3}
        value={paragraph.evidence}
      />
      <TextAreaField
        id={`leq-explanation-${id}`}
        label={strings.explanationLabel}
        onChange={(explanation) => onChange({ explanation })}
        placeholder={strings.explanationPlaceholder}
        prose
        rows={3}
        value={paragraph.explanation}
      />
      {removable ? (
        <button
          aria-label={t(strings.removeParagraphLabel, { n: fmt(index + 1) })}
          className={BUTTON_SMALL}
          onClick={onRemove}
          type="button"
        >
          {strings.removeParagraph}
        </button>
      ) : null}
    </fieldset>
  );
}

export default function LeqOutlineGeneratorWidget() {
  const strings = useToolUi(LEQ_OUTLINE_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const nextId = useRef(DEFAULT_PARAGRAPHS);
  const fresh = (): LeqParagraph => {
    nextId.current += 1;
    return blankParagraph(nextId.current);
  };
  const [fields, setFields] = useState(BLANK);
  const [process, setProcess] = useState<ReasoningProcess>("causation");
  const [paragraphs, setParagraphs] =
    useState<LeqParagraph[]>(initialParagraphs);
  const input: LeqInput = useMemo(
    () => ({ ...fields, process, paragraphs }),
    [fields, process, paragraphs]
  );
  const doc = useMemo(() => buildLeqOutline(input, strings), [input, strings]);
  const frames = buildLeqThesisFrames(
    process,
    paragraphs,
    strings.and,
    (n) => t(strings.categoryStand, { n: fmt(n) }),
    LEQ_THESIS_FRAMES[process]
  );
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const patch = (id: number, change: Partial<LeqParagraph>) =>
    setParagraphs((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, ...change } : entry
      )
    );
  const moveOptions: { value: LeqComplexityMove; label: string }[] = [
    { value: "", label: strings.complexityNone },
    ...LEQ_COMPLEXITY_MOVES.map((value) => ({
      value,
      label: strings.complexityMoves[value],
    })),
  ];

  function loadExample() {
    const { example } = strings;
    const exampleParagraphs = leqExampleParagraphs(strings);
    const { process: exampleProcess, ...rest } = example;
    setFields({ ...BLANK, ...rest });
    setProcess(exampleProcess);
    setParagraphs(exampleParagraphs.map((entry) => ({ ...fresh(), ...entry })));
  }

  function clear() {
    setFields(BLANK);
    setParagraphs(initialParagraphs());
    nextId.current = DEFAULT_PARAGRAPHS;
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.promptHeading}>
          <TextAreaField
            id="leq-prompt"
            label={strings.promptLabel}
            onChange={update("prompt")}
            placeholder={strings.promptPlaceholder}
            prose
            rows={2}
            value={fields.prompt}
          />
          <PillGroup
            label={strings.processLabel}
            name="leq-process"
            onChange={setProcess}
            options={REASONING_PROCESSES.map((value) => ({
              value,
              label: strings.processes[value],
            }))}
            value={process}
          />
          <p className={HINT}>{strings.processHint}</p>
        </Panel>
        <Panel title={strings.introHeading}>
          <TextAreaField
            hint={strings.contextHint}
            id="leq-context"
            label={strings.contextLabel}
            onChange={update("context")}
            placeholder={strings.contextPlaceholder}
            prose
            rows={3}
            value={fields.context}
          />
          <TextAreaField
            id="leq-thesis"
            label={strings.thesisLabel}
            onChange={update("thesis")}
            placeholder={strings.thesisPlaceholder}
            prose
            rows={3}
            value={fields.thesis}
          />
        </Panel>
        <section className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.framesHeading}</h2>
          <FrameList
            copiedLabel={strings.copied}
            copyLabel={strings.copy}
            frames={frames}
            intro={strings.framesIntro}
            labels={strings.frameLabels}
            need={strings.framesNeed}
            onUse={update("thesis")}
            useLabel={strings.useAsThesis}
          />
        </section>
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.bodyHeading}</h2>
          {paragraphs.map((paragraph, index) => (
            <ParagraphFields
              index={index}
              key={paragraph.id}
              onChange={(change) => patch(paragraph.id, change)}
              onRemove={() =>
                setParagraphs((previous) =>
                  previous.filter((entry) => entry.id !== paragraph.id)
                )
              }
              paragraph={paragraph}
              removable={paragraphs.length > LEQ_MIN_PARAGRAPHS}
              strings={strings}
            />
          ))}
          {paragraphs.length < LEQ_MAX_PARAGRAPHS ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setParagraphs((previous) => [...previous, fresh()])
              }
              type="button"
            >
              {strings.addParagraph}
            </button>
          ) : null}
        </section>
        <Panel title={strings.complexityHeading}>
          <SelectField
            id="leq-move"
            label={strings.complexityMoveLabel}
            onChange={(complexityMove) =>
              setFields((previous) => ({ ...previous, complexityMove }))
            }
            options={moveOptions}
            value={fields.complexityMove}
          />
          <TextAreaField
            id="leq-complexity"
            label={strings.complexityNoteLabel}
            onChange={update("complexityNote")}
            placeholder={strings.complexityNotePlaceholder}
            prose
            rows={2}
            value={fields.complexityNote}
          />
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="leq"
        labels={strings.document}
      />
    </div>
  );
}
